import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Lock,
  User,
  Upload,
  Pencil,
  Check,
  X,
  ExternalLink,
  Globe,
  Palette,
  Copy,
  Mail,
} from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { useUpdatePassword, useUpdateProfile } from "@/hooks/use-auth";
import {
  useDiscordStatus,
  useCreateDiscordLinkSession,
  useDisconnectDiscord,
  useNotificationSettings,
  useUpdateNotificationSettings,
} from "@/hooks/use-discord";
import { useToast } from "@/hooks/use-toast";
import { useI18n, languageOptions } from "@/lib/i18n";
import { AppLayout } from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { PersonalProviders } from "@/components/ai/personal-providers";
import { ThemeSelector } from "@/components/ui/theme-selector";

// Discord Icon component
const DiscordIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
  </svg>
);

// Notification type keys - labels come from i18n
const notificationTypeKeys = [
  {
    key: "TaskAssigned",
    i18nLabel: "notifications.taskAssigned",
    i18nDesc: "notifications.taskAssignedDesc",
  },
  {
    key: "TaskUpdated",
    i18nLabel: "notifications.taskUpdated",
    i18nDesc: "notifications.taskUpdatedDesc",
  },
  {
    key: "CommentAdded",
    i18nLabel: "notifications.commentAdded",
    i18nDesc: "notifications.commentAddedDesc",
  },
  {
    key: "AttachmentAdded",
    i18nLabel: "notifications.attachmentAdded",
    i18nDesc: "notifications.attachmentAddedDesc",
  },
  {
    key: "Mention",
    i18nLabel: "notifications.mentioned",
    i18nDesc: "notifications.mentionedDesc",
  },
  {
    key: "ProjectInvite",
    i18nLabel: "notifications.projectInvite",
    i18nDesc: "notifications.projectInviteDesc",
  },
  {
    key: "DueDateReminder",
    i18nLabel: "notifications.dueDateReminder",
    i18nDesc: "notifications.dueDateReminderDesc",
  },
  {
    key: "TaskOverdue",
    i18nLabel: "notifications.taskOverdue",
    i18nDesc: "notifications.taskOverdueDesc",
  },
];

// Reminder timing option keys
const reminderTimingKeys = [
  { value: "1h", i18nLabel: "settings.before1h" },
  { value: "24h", i18nLabel: "settings.before24h" },
  { value: "48h", i18nLabel: "settings.before48h" },
  { value: "24h_after", i18nLabel: "settings.after24h" },
  { value: "48h_after", i18nLabel: "settings.after48h" },
];

export function SettingsPage() {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const { t, language, setLanguage } = useI18n();
  const updatePassword = useUpdatePassword();
  const updateProfile = useUpdateProfile();
  const [searchParams] = useSearchParams();

  // Discord hooks
  const {
    data: discordStatus,
    isLoading: discordLoading,
    refetch: refetchDiscordStatus,
  } = useDiscordStatus();
  const createDiscordLinkSession = useCreateDiscordLinkSession();
  const disconnectDiscord = useDisconnectDiscord();
  const { data: notificationSettings } = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(user?.name || "");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [selectedReminders, setSelectedReminders] = useState<string[]>(["24h"]);
  const [discordLinkSession, setDiscordLinkSession] = useState<{
    code: string;
    expiresAt: string;
    installUrl: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle Discord OAuth callback
  useEffect(() => {
    const discordParam = searchParams.get("discord");
    if (discordParam === "success") {
      toast({
        title: "Discord Connected!",
        description: "Your Discord account has been successfully linked.",
      });
      window.history.replaceState({}, "", "/settings");
    } else if (discordParam === "error") {
      const message = searchParams.get("message") || "Unknown error";
      toast({
        title: "Discord Connection Failed",
        description: `Error: ${message.replace(/_/g, " ")}`,
        variant: "destructive",
      });
      window.history.replaceState({}, "", "/settings");
    }
  }, [searchParams, toast]);

  useEffect(() => {
    if (!discordLinkSession) return;

    const refreshStatus = window.setInterval(() => {
      void refetchDiscordStatus();
    }, 3000);
    const expiresIn = Math.max(
      0,
      new Date(discordLinkSession.expiresAt).getTime() - Date.now(),
    );
    const expiration = window.setTimeout(
      () => setDiscordLinkSession(null),
      expiresIn,
    );

    return () => {
      window.clearInterval(refreshStatus);
      window.clearTimeout(expiration);
    };
  }, [discordLinkSession, refetchDiscordStatus]);

  useEffect(() => {
    if (!discordLinkSession || !discordStatus?.deliveryReady) return;
    setDiscordLinkSession(null);
    toast({
      title: t("settings.discordReady"),
      description: t("settings.discordReadyDescription"),
    });
  }, [discordLinkSession, discordStatus?.deliveryReady, t, toast]);

  // Parse reminder timing from settings
  useEffect(() => {
    if (notificationSettings?.reminderTiming) {
      const timings = notificationSettings.reminderTiming
        .split(",")
        .map((t) => t.trim());
      setSelectedReminders(timings);
    }
  }, [notificationSettings?.reminderTiming]);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: t("common.error"),
        description: t("auth.passwordsDoNotMatch"),
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: t("common.error"),
        description: t("auth.passwordMinLength"),
        variant: "destructive",
      });
      return;
    }

    try {
      await updatePassword.mutateAsync({
        currentPassword,
        newPassword,
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      toast({
        title: t("common.success"),
        description: t("success.updated"),
      });
    } catch {
      // Error is already handled by the hook
    }
  };

  const handleNameUpdate = async () => {
    if (!editedName.trim() || editedName.trim().length < 2) {
      toast({
        title: t("common.error"),
        description: t("auth.nameRequired"),
        variant: "destructive",
      });
      return;
    }

    try {
      await updateProfile.mutateAsync({ name: editedName.trim() });
      setIsEditingName(false);
    } catch {
      // Error is already handled by the hook
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: t("common.error"),
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: t("common.error"),
        description: "Image must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingAvatar(true);

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        await updateProfile.mutateAsync({ avatarUrl: base64String });
        setIsUploadingAvatar(false);
      };
      reader.onerror = () => {
        toast({
          title: t("common.error"),
          description: "Failed to read image file",
          variant: "destructive",
        });
        setIsUploadingAvatar(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setIsUploadingAvatar(false);
    }
  };

  const handleNotificationToggle = async (
    platform: "discord" | "inApp" | "email",
    type: string,
    enabled: boolean,
  ) => {
    const key = `${platform}${type}` as keyof typeof notificationSettings;
    await updateSettings.mutateAsync({ [key]: enabled });
  };

  const handleMasterToggle = async (
    platform: "discord" | "inApp" | "email",
    enabled: boolean,
  ) => {
    const key = (
      platform === "email" ? "emailFallbackEnabled" : `${platform}Enabled`
    ) as keyof typeof notificationSettings;
    await updateSettings.mutateAsync({ [key]: enabled });
  };

  const handleStartDiscordLink = async () => {
    const session = await createDiscordLinkSession.mutateAsync();
    setDiscordLinkSession(session);
  };

  const handleCopyDiscordCode = async () => {
    if (!discordLinkSession) return;
    await navigator.clipboard.writeText(discordLinkSession.code);
    toast({ title: t("settings.codeCopied") });
  };

  const handleReminderTimingChange = async (
    value: string,
    checked: boolean,
  ) => {
    let newTimings: string[];
    if (checked) {
      newTimings = [...selectedReminders, value];
    } else {
      newTimings = selectedReminders.filter((t) => t !== value);
    }

    if (newTimings.length === 0) {
      newTimings = ["24h"];
    }

    setSelectedReminders(newTimings);
    await updateSettings.mutateAsync({ reminderTiming: newTimings.join(",") });
  };

  return (
    <AppLayout>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-6 sm:mb-8 rounded-xl backdrop-blur-md bg-white/5 border border-white/10 p-4 sm:p-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              {t("settings.title")}
            </h1>
            <p className="mt-1 text-sm sm:text-base text-white/70">
              {t("settings.notificationSettings")}
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-start gap-6">
            <nav className="md:sticky md:top-4 md:w-60 w-full min-w-0 shrink-0 flex md:flex-col gap-1 overflow-x-auto md:overflow-x-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur-md p-2">
              {[
                ["profile", t("settings.profile")],
                ["language", t("settings.language.title")],
                ["appearance", t("settings.themeTitle")],
                ["discord", t("settings.discordIntegration")],
                ["notifications", t("settings.notificationSettings")],
                ["security", t("settings.changePassword")],
                ["ai", language === "fr" ? "Fournisseurs IA" : "AI providers"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="rounded-lg px-3 py-2.5 text-sm text-foreground-secondary hover:bg-primary/10 hover:text-primary transition-colors whitespace-nowrap md:whitespace-normal md:break-words"
                >
                  {label}
                </a>
              ))}
            </nav>
            <div className="min-w-0 flex-1 space-y-4 sm:space-y-6 [&>section]:scroll-mt-4 [&>div]:scroll-mt-4">
              {/* Profile Information */}
              <Card id="profile">
                <CardHeader className="pb-4 sm:pb-6">
                  <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    <User className="h-5 w-5" />
                    {t("settings.profileInfo")}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {t("settings.profile")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6">
                  {/* Avatar Upload */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                    <Avatar className="h-20 w-20 sm:h-24 sm:w-24">
                      <AvatarImage src={user?.avatarUrl || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xl sm:text-2xl">
                        {user?.name ? getInitials(user.name) : "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-2">
                      <Label>{t("settings.profilePicture")}</Label>
                      <div className="flex flex-wrap gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarUpload}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={
                            isUploadingAvatar || updateProfile.isPending
                          }
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {isUploadingAvatar
                            ? t("common.loading")
                            : t("settings.uploadPicture")}
                        </Button>
                        {user?.avatarUrl && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              updateProfile.mutateAsync({ avatarUrl: null })
                            }
                            disabled={
                              isUploadingAvatar || updateProfile.isPending
                            }
                          >
                            {t("settings.removePicture")}
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-foreground-muted">
                        {t("settings.uploadProfilePictureHint")}
                      </p>
                    </div>
                  </div>

                  {/* Name */}
                  <div>
                    <Label>{t("common.name")}</Label>
                    <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
                      {isEditingName ? (
                        <>
                          <Input
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleNameUpdate();
                              if (e.key === "Escape") {
                                setEditedName(user?.name || "");
                                setIsEditingName(false);
                              }
                            }}
                            autoFocus
                            className="flex-1"
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={handleNameUpdate}
                              disabled={updateProfile.isPending}
                              className="flex-1 sm:flex-none"
                            >
                              {t("common.save")}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditedName(user?.name || "");
                                setIsEditingName(false);
                              }}
                              className="flex-1 sm:flex-none"
                            >
                              {t("common.cancel")}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <Input
                            value={user?.name || ""}
                            disabled
                            className="flex-1"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditedName(user?.name || "");
                              setIsEditingName(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <Label>{t("common.email")}</Label>
                    <Input
                      value={user?.email || ""}
                      disabled
                      className="mt-1.5 bg-white/5"
                    />
                    <p className="mt-1.5 text-xs text-white/50">
                      {t("settings.emailCannotBeChanged")}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Language Settings */}
              <Card id="language">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    <Globe className="h-5 w-5" />
                    {t("settings.language.title")}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {t("settings.language.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="language-select">
                        {t("settings.language.select")}
                      </Label>
                      <Select
                        value={language}
                        onValueChange={(value: "en" | "fr") =>
                          setLanguage(value)
                        }
                      >
                        <SelectTrigger
                          className="w-full sm:w-64 mt-1.5"
                          id="language-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {languageOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <span className="flex items-center gap-2">
                                <span>{option.flag}</span>
                                <span>{option.label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Theme Settings */}
              <Card id="appearance">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    <Palette className="h-5 w-5" />
                    {t("settings.themeTitle")}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {t("settings.themeDescription")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ThemeSelector />
                </CardContent>
              </Card>

              {/* Discord Integration */}
              <Card id="discord">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                    <DiscordIcon className="h-5 w-5" />
                    {t("settings.discordIntegration")}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {t("settings.discordIntegrationDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {discordLoading ? (
                    <div className="flex items-center gap-2 text-foreground-muted">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      {t("common.loading")}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {discordStatus?.connected ? (
                        <div
                          className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-3 sm:p-4 rounded-lg border ${discordStatus.deliveryReady ? "bg-green-500/10 border-green-500/20" : "bg-amber-500/10 border-amber-500/20"}`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`h-10 w-10 rounded-full flex items-center justify-center ${discordStatus.deliveryReady ? "bg-green-500/20" : "bg-amber-500/20"}`}
                            >
                              {discordStatus.deliveryReady ? (
                                <Check className="h-5 w-5 text-green-500" />
                              ) : (
                                <DiscordIcon className="h-5 w-5 text-amber-500" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-foreground">
                                {discordStatus.deliveryReady
                                  ? t("settings.discordReady")
                                  : t("settings.discordNeedsReconnect")}
                              </p>
                              <p className="text-sm text-foreground-secondary">
                                {discordStatus.discordUsername}
                              </p>
                              {!discordStatus.deliveryReady &&
                                discordStatus.lastError && (
                                  <p className="mt-1 text-xs text-amber-400">
                                    {discordStatus.lastError}
                                  </p>
                                )}
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2">
                            {!discordStatus.deliveryReady && (
                              <Button
                                size="sm"
                                onClick={handleStartDiscordLink}
                                disabled={createDiscordLinkSession.isPending}
                                className="bg-[#5865F2] hover:bg-[#4752C4]"
                              >
                                {t("settings.reconnectDiscord")}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => disconnectDiscord.mutate()}
                              disabled={disconnectDiscord.isPending}
                              className="text-destructive hover:text-destructive"
                            >
                              <X className="h-4 w-4 mr-2" />
                              {t("settings.disconnectDiscord")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        !discordLinkSession && (
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-3 sm:p-4 rounded-lg backdrop-blur-md bg-white/5 border border-white/10">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-full bg-[#5865F2]/20 flex items-center justify-center">
                                <DiscordIcon className="h-5 w-5 text-[#5865F2]" />
                              </div>
                              <div>
                                <p className="font-medium text-foreground">
                                  {t("settings.connectDiscord")}
                                </p>
                                <p className="text-sm text-foreground-secondary">
                                  {t("settings.linkDiscordDesc")}
                                </p>
                              </div>
                            </div>
                            <Button
                              onClick={handleStartDiscordLink}
                              disabled={createDiscordLinkSession.isPending}
                              className="bg-[#5865F2] hover:bg-[#4752C4] w-full sm:w-auto"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              {t("settings.connectDiscord")}
                            </Button>
                          </div>
                        )
                      )}

                      {discordLinkSession && (
                        <div className="rounded-lg border border-[#5865F2]/30 bg-[#5865F2]/10 p-4 space-y-4">
                          <ol className="space-y-2 text-sm text-foreground-secondary list-decimal list-inside">
                            <li>{t("settings.discordLinkStepInstall")}</li>
                            <li>{t("settings.discordLinkStepCommand")}</li>
                          </ol>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <div className="flex flex-1 items-center justify-between rounded-md border border-white/10 bg-black/20 px-4 py-3 font-mono text-lg tracking-widest">
                              <span>{discordLinkSession.code}</span>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleCopyDiscordCode}
                                aria-label={t("settings.copyCode")}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                            <Button
                              onClick={() =>
                                window.open(
                                  discordLinkSession.installUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                              className="bg-[#5865F2] hover:bg-[#4752C4]"
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              {t("settings.installDiscordApp")}
                            </Button>
                          </div>
                          <p className="text-xs text-foreground-muted">
                            {t("settings.discordCommandHint").replace(
                              "{code}",
                              discordLinkSession.code,
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notification Settings */}
              {notificationSettings && (
                <Card id="notifications">
                  <CardHeader>
                    <CardTitle className="text-lg sm:text-xl">
                      {t("settings.notificationSettings")}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {t("settings.chooseNotifications")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Master toggles */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-3 sm:p-4 rounded-lg backdrop-blur-md bg-white/5 border border-white/10">
                      <div className="flex items-center justify-between sm:justify-start gap-4 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center">
                            📱
                          </div>
                          <span className="font-medium text-sm sm:text-base">
                            {t("settings.inAppNotifications")}
                          </span>
                        </div>
                        <Switch
                          checked={notificationSettings.inAppEnabled}
                          onCheckedChange={(checked) =>
                            handleMasterToggle("inApp", checked)
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between sm:justify-start gap-4 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg bg-[#5865F2]/20 flex items-center justify-center">
                            <DiscordIcon className="h-4 w-4 text-[#5865F2]" />
                          </div>
                          <span className="font-medium text-sm sm:text-base">
                            {t("settings.discordNotifications")}
                          </span>
                        </div>
                        <Switch
                          checked={notificationSettings.discordEnabled}
                          onCheckedChange={(checked) =>
                            handleMasterToggle("discord", checked)
                          }
                          disabled={!discordStatus?.deliveryReady}
                        />
                      </div>
                      <div className="flex items-center justify-between sm:justify-start gap-4 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Mail className="h-4 w-4 text-blue-400" />
                          </div>
                          <div>
                            <span className="font-medium text-sm sm:text-base">
                              {t("settings.emailFallbackNotifications")}
                            </span>
                            <p className="text-xs text-foreground-muted">
                              {t("settings.emailFallbackDescription")}
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={notificationSettings.emailFallbackEnabled}
                          onCheckedChange={(checked) =>
                            handleMasterToggle("email", checked)
                          }
                        />
                      </div>
                    </div>

                    {/* Notification types */}
                    <div className="space-y-3">
                      {notificationTypeKeys.map((type) => {
                        const inAppKey =
                          `inApp${type.key}` as keyof typeof notificationSettings;
                        const discordKey =
                          `discord${type.key}` as keyof typeof notificationSettings;
                        const emailKey =
                          `email${type.key}` as keyof typeof notificationSettings;

                        return (
                          <div
                            key={type.key}
                            className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-lg border border-border gap-3 sm:gap-0"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-foreground text-sm sm:text-base">
                                {t(type.i18nLabel as keyof typeof t)}
                              </p>
                              <p className="text-xs sm:text-sm text-foreground-muted">
                                {t(type.i18nDesc as keyof typeof t)}
                              </p>
                            </div>
                            <div className="flex items-center gap-4 sm:gap-6">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Switch
                                  checked={
                                    notificationSettings[inAppKey] as boolean
                                  }
                                  onCheckedChange={(checked) =>
                                    handleNotificationToggle(
                                      "inApp",
                                      type.key,
                                      checked,
                                    )
                                  }
                                  disabled={!notificationSettings.inAppEnabled}
                                />
                                <span className="text-xs text-foreground-muted">
                                  App
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Switch
                                  checked={
                                    notificationSettings[discordKey] as boolean
                                  }
                                  onCheckedChange={(checked) =>
                                    handleNotificationToggle(
                                      "discord",
                                      type.key,
                                      checked,
                                    )
                                  }
                                  disabled={
                                    !notificationSettings.discordEnabled ||
                                    !discordStatus?.deliveryReady
                                  }
                                />
                                <span className="text-xs text-foreground-muted">
                                  Discord
                                </span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <Switch
                                  checked={
                                    notificationSettings[emailKey] as boolean
                                  }
                                  onCheckedChange={(checked) =>
                                    handleNotificationToggle(
                                      "email",
                                      type.key,
                                      checked,
                                    )
                                  }
                                  disabled={
                                    !notificationSettings.emailFallbackEnabled
                                  }
                                />
                                <span className="text-xs text-foreground-muted">
                                  E-mail
                                </span>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Reminder timing */}
                    <div className="pt-4 border-t border-border">
                      <Label className="text-sm font-medium">
                        {t("settings.reminderTiming")}
                      </Label>
                      <p className="text-xs sm:text-sm text-foreground-muted mb-4">
                        {t("settings.reminderTimingDesc")}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {reminderTimingKeys.map((option) => (
                          <label
                            key={option.value}
                            className="flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:bg-white/10 cursor-pointer transition-colors"
                          >
                            <Checkbox
                              checked={selectedReminders.includes(option.value)}
                              onCheckedChange={(checked) =>
                                handleReminderTimingChange(
                                  option.value,
                                  checked as boolean,
                                )
                              }
                            />
                            <span className="text-sm">
                              {t(option.i18nLabel as keyof typeof t)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Change Password */}
              <Card id="security">
                <form onSubmit={handlePasswordChange}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                      <Lock className="h-5 w-5" />
                      {t("settings.changePassword")}
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {t("settings.updatePassword")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="current-password">
                        {t("auth.currentPassword")}
                      </Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="new-password">
                        {t("auth.newPassword")}
                      </Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                        className="mt-1.5"
                      />
                      <p className="mt-1.5 text-xs text-foreground-muted">
                        {t("auth.password8Chars")}
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="confirm-password">
                        {t("auth.confirmPassword")}
                      </Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        className="mt-1.5"
                      />
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      type="submit"
                      disabled={updatePassword.isPending}
                      className="w-full sm:w-auto"
                    >
                      {updatePassword.isPending
                        ? t("common.loading")
                        : t("settings.updatePassword")}
                    </Button>
                  </CardFooter>
                </form>
              </Card>
              <Card id="ai">
                <CardHeader>
                  <CardTitle>
                    {language === "fr"
                      ? "Mes fournisseurs IA"
                      : "My AI providers"}
                  </CardTitle>
                  <CardDescription>
                    {language === "fr"
                      ? "Vos clés restent personnelles. Partagez un modèle avec un projet depuis les paramètres du Chat AI."
                      : "Your keys remain personal. Share a model from each project’s Chat AI settings."}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PersonalProviders />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
